import { BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch } from "@/api/generated/types.gen";

type UpdatableAssessmentField = keyof Omit<
  BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
  "case_id"
>;

// Every column derived from the transcript — the transcript itself, the
// AI summary, and every report section. `notes` is deliberately excluded:
// it's user-typed, not transcript-derived, matching the MDRS-132
// overwrite-reset precedent (`build_assessment_reset_kwargs` in
// backend/utils/assessment.py). Mirrors `AssessmentColumnNameEnum` in
// backend/models/case.py — if a column is added there, add it here too.
const TRANSCRIPT_DERIVED_FIELDS = [
  "full_transcript",
  "segmented_full_transcript",
  "one_line_summary",
  "full_summary",
  "history_of_injury",
  "history_of_treatment",
  "history_of_progress_over_time",
  "other_relevant_history",
  "educational_occupational_background",
  "activities_of_daily_living",
  "medication",
  "physical_examination",
  "mental_state_examination",
  "current_injury_status",
  "past_medical_history",
  "psychological_history",
  "work_status_and_capacity",
  "psychological_factors_affecting_capacity",
  "current_physical_capacity",
  "current_cognitive_capacity",
  "current_work_capacity_as_reported",
  "rehabilitation_potential_and_barriers",
  "military_service_history",
  "pre_service_background_and_baseline",
  "early_life_and_developmental_history",
  "disclosure_history",
  "treating_relationship_and_informed_consent",
  "immediate_consequences_and_early_management",
  "psychological_and_emotional_impact",
  "sleep_and_fatigue",
  "impact_on_relationships_and_intimacy",
  "prognosis_and_ongoing_suffering",
] as const satisfies readonly Exclude<
  UpdatableAssessmentField,
  "notes" | "status"
>[];

/**
 * MDRS-399 — "Delete transcript" clears the transcript AND every
 * summary/report-section column derived from it, rather than hard-deleting
 * the assessment row. Shaped as the single PATCH body `useUpdateAssessment`
 * sends — one bulk request instead of one PATCH per field. History is
 * preserved server-side by the existing update flow.
 */
export const buildTranscriptResetPayload = (): Partial<
  Record<(typeof TRANSCRIPT_DERIVED_FIELDS)[number], "">
> =>
  Object.fromEntries(
    TRANSCRIPT_DERIVED_FIELDS.map((field) => [field, ""])
  ) as Record<(typeof TRANSCRIPT_DERIVED_FIELDS)[number], "">;
